import React from "react";
import SEO from "@/components/SEO";
import { siteConfig } from "@/config/siteConfig";

const PrivacyPolicy = () => (
  <>
    <SEO
      title="Privacy Policy"
      description={`Privacy practices of ${siteConfig.COMPANY_NAME}, including data collected via forms, cookies and analytics, retention, and user rights under GDPR and UAE PDPL.`}
      path="/privacy-policy"
    />
    <article className="mx-auto max-w-3xl px-6 lg:px-10 py-16 lg:py-24 legal-doc" data-testid="privacy-policy-page">
      <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[var(--navy)] tracking-tight">
        Privacy Policy
      </h1>
      <p className="mt-3 text-sm text-[var(--slate)]">
        Last updated: {siteConfig.CURRENT_YEAR}
      </p>

      <p className="mt-8">
        This Privacy Policy explains how <strong>{siteConfig.COMPANY_NAME}</strong> (“we”, “us”, “our”),
        headquartered in {siteConfig.ADDRESS_CITY}, collects, uses and safeguards personal data provided by
        visitors of this website (“Site”). It also describes the choices available to you regarding the use of your
        personal data. This notice is written for a general business audience and does not constitute legal advice.
      </p>

      <h2>1. Who we are</h2>
      <p>
        {siteConfig.COMPANY_NAME} supplies specialised printing inks, coding consumables and related production
        solutions supplied for pharmaceutical manufacturing applications, primarily serving pharmaceutical manufacturers
        and their distribution partners worldwide.
      </p>

      <h2>2. What information we collect</h2>
      <ul>
        <li><strong>Information you provide through forms</strong> — such as name, company, country, business email, phone number, and the details of your enquiry (e.g. sample requests, distributor enquiries, technical support requests).</li>
        <li><strong>Communications</strong> — records of correspondence you send us via email, phone, WhatsApp or the contact form.</li>
        <li><strong>Technical data</strong> — IP address, device type, browser, operating system, and pages visited on the Site.</li>
        <li><strong>Cookies and analytics</strong> — see our <a href="/cookie-policy">Cookie Policy</a> for details of the cookies set on this Site and how consent is managed.</li>
      </ul>
      <p>We do not knowingly collect information from children. This Site is intended for business use.</p>

      <h2>3. How we use your information</h2>
      <ul>
        <li>To respond to your enquiries, sample requests and quotations.</li>
        <li>To fulfil orders and administer commercial relationships with distributors and partners.</li>
        <li>To provide technical product information (subject to application testing on your side).</li>
        <li>To improve the Site, our product content and customer experience.</li>
        <li>To comply with applicable legal, tax and regulatory obligations in the United Arab Emirates and, where relevant, in the country of the recipient.</li>
      </ul>

      <h2>4. Legal bases</h2>
      <p>
        Where the EU/UK GDPR applies, we rely on: (a) your consent (for optional analytics cookies and marketing
        communications where relevant), (b) the necessity to take steps at your request before entering into a
        contract, (c) our legitimate interests in operating and improving a B2B website, and (d) compliance with
        legal obligations. Under the UAE Personal Data Protection Law (Federal Decree-Law No. 45 of 2021, “PDPL”),
        we process personal data on comparable lawful bases.
      </p>

      <h2>5. Sharing of information</h2>
      <p>We do not sell personal data. We may share it with:</p>
      <ul>
        <li>Specialist manufacturing and international supply partners, strictly to fulfil your enquiry or order.</li>
        <li>Service providers (hosting, analytics, email delivery, CRM) that process data on our behalf under contract.</li>
        <li>Public authorities where required by law.</li>
      </ul>
      <p>Any international transfer will use appropriate safeguards recognised under applicable law.</p>

      <h2>6. Retention</h2>
      <p>
        We retain personal data only for as long as necessary to fulfil the purposes it was collected for, including
        satisfying any legal, accounting, or reporting requirements. Enquiry data is typically retained for up to
        24 months from last contact unless a longer period is required.
      </p>

      <h2>7. Your rights</h2>
      <p>
        Subject to applicable law (GDPR, UAE PDPL and equivalents), you have rights of access, rectification, erasure,
        restriction, objection, and data portability, as well as the right to withdraw consent at any time where
        processing is based on consent. You may exercise these rights by contacting us at{" "}
        <strong>{siteConfig.EMAIL_SALES}</strong>. You also have the right to lodge a complaint with a supervisory
        authority in your jurisdiction.
      </p>

      <h2>8. Security</h2>
      <p>
        We apply reasonable administrative, technical and organisational measures to protect personal data. However,
        no method of transmission over the internet is entirely secure.
      </p>

      <h2>9. Changes to this policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Material changes will be communicated on this page with
        an updated “last updated” date.
      </p>

      <h2>10. Contact</h2>
      <p>
        Questions or data subject requests can be sent to <strong>{siteConfig.EMAIL_SALES}</strong> or by post to
        {" "}{siteConfig.COMPANY_NAME}, {siteConfig.ADDRESS_STREET}, {siteConfig.ADDRESS_CITY}.
      </p>
    </article>
  </>
);

export default PrivacyPolicy;
